import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import CalculateIcon from '@mui/icons-material/Calculate';
import { useDroppable } from '@dnd-kit/core';
import { usePlanning } from '../context/PlanningContext';
import type { Risorsa } from '../types';
import BudgetProgressBarCompact from './BudgetProgressBarCompact';

interface MonthGridProps {
  risorsa: Risorsa;
}

const MESI = [
  'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
];

interface MonthBoxProps {
  risorsa: Risorsa;
  mese: number;
  meseNome: string;
  isReadOnly: boolean;
}

const MonthBox: React.FC<MonthBoxProps> = ({ risorsa, mese, meseNome, isReadOnly }) => {
  const { allocazioni, progetti, anno, deleteAllocazione, saveAllocazione, refreshProgetti, risorse } = usePlanning();
  const [error, setError] = useState<string>('');
  const [editingAllocazione, setEditingAllocazione] = useState<{ id_progetto: number; percentuale: number } | null>(null);
  const [editPercentuale, setEditPercentuale] = useState<string>('');
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedRisorsaCopy, setSelectedRisorsaCopy] = useState<number>(risorsa.id);
  const [selectedMeseCopy, setSelectedMeseCopy] = useState<number>(mese - 1);

  const { setNodeRef, isOver } = useDroppable({
    id: `month-${risorsa.id}-${mese}`,
    data: { risorsa, mese },
    disabled: isReadOnly,
  });

  // Allocazioni per questo mese e risorsa
  const allocazioniMese = useMemo(() => {
    return allocazioni.filter(
      (a) => a.id_risorsa === risorsa.id && a.mese === mese && a.anno === anno
    );
  }, [allocazioni, risorsa.id, mese, anno]);

  // Totale percentuale del mese
  const totaleMese = useMemo(() => {
    return allocazioniMese.reduce((sum, a) => sum + a.percentuale, 0);
  }, [allocazioniMese]);

  const getProgettoNome = (id: number): string => {
    const progetto = progetti.find((p) => p.id === id);
    if (!progetto) return 'N/A';
    return progetto.id_hrp ? `[${progetto.id_hrp}] ${progetto.titolo}` : progetto.titolo;
  };

  const handleDelete = async (idProgetto: number) => {
    try {
      await deleteAllocazione(risorsa.id, idProgetto, mese);
      setError('');
    } catch (err: any) {
      setError('Errore nell\'eliminazione');
    }
  };

  const handleEditClick = (idProgetto: number, currentPercentuale: number) => {
    setEditingAllocazione({ id_progetto: idProgetto, percentuale: currentPercentuale });
    setEditPercentuale(currentPercentuale.toString());
  };

  const handleSaveEdit = async () => {
    if (!editingAllocazione) return;

    const perc = parseInt(editPercentuale);
    if (isNaN(perc) || perc < 0) {
      setError('Percentuale deve essere maggiore o uguale a 0');
      return;
    }

    try {
      await saveAllocazione({
        id_risorsa: risorsa.id,
        id_progetto: editingAllocazione.id_progetto,
        anno,
        mese,
        percentuale: perc,
      });
      // Refresh progetti per aggiornare i dati del budget
      await refreshProgetti();
      setEditingAllocazione(null);
      setEditPercentuale('');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio');
    }
  };

  const handleCancelEdit = () => {
    setEditingAllocazione(null);
    setEditPercentuale('');
    setError('');
  };

  const handleQuickPercentuale = async (perc: number) => {
    if (!editingAllocazione) return;

    try {
      await saveAllocazione({
        id_risorsa: risorsa.id,
        id_progetto: editingAllocazione.id_progetto,
        anno,
        mese,
        percentuale: perc,
      });
      // Refresh progetti per aggiornare i dati del budget
      await refreshProgetti();
      setEditingAllocazione(null);
      setEditPercentuale('');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio');
    }
  };

  const handleOpenCopyDialog = () => {
    setSelectedRisorsaCopy(risorsa.id);
    setSelectedMeseCopy(mese > 1 ? mese - 1 : 1);
    setCopyDialogOpen(true);
  };

  const handleCopyFromSelected = async () => {
    // Trova allocazioni del mese sorgente per la risorsa selezionata
    const allocazioniSorgente = allocazioni.filter(
      (a) => a.id_risorsa === selectedRisorsaCopy && a.mese === selectedMeseCopy && a.anno === anno
    );

    // ID progetti già allocati nel mese corrente
    const progettiGiaAllocati = allocazioniMese.map(a => a.id_progetto);

    // Copia ogni allocazione nel mese corrente, skippando quelle già presenti
    let copiedCount = 0;
    let skippedCount = 0;

    try {
      for (const alloc of allocazioniSorgente) {
        // Skip se il progetto è già allocato nel mese corrente
        if (progettiGiaAllocati.includes(alloc.id_progetto)) {
          skippedCount++;
          continue;
        }

        await saveAllocazione({
          id_risorsa: risorsa.id,
          id_progetto: alloc.id_progetto,
          anno,
          mese,
          percentuale: alloc.percentuale,
        });
        copiedCount++;
      }

      setCopyDialogOpen(false);
      setError('');

      // Aggiorna i totali budget/residuo come avviene dopo il save manuale
      if (copiedCount > 0) {
        await refreshProgetti();
      }

      if (copiedCount > 0 || skippedCount > 0) {
        console.log(`Copia completata: ${copiedCount} progetti copiati, ${skippedCount} già presenti (skippati)`);
      }
    } catch (err: any) {
      setError(err.message || 'Errore nella copia');
    }
  };

  const handleClearMonth = async () => {
    // Cancella tutte le allocazioni del mese corrente
    try {
      for (const alloc of allocazioniMese) {
        await deleteAllocazione(risorsa.id, alloc.id_progetto, mese);
      }
      setError('');
    } catch (err: any) {
      setError('Errore nella cancellazione');
    }
  };

  const handleAutoCalculate = async () => {
    // Calcola il residuo: 100% - somma delle percentuali già assegnate (> 0)
    const progettiConPercentuale = allocazioniMese.filter(a => a.percentuale > 0);
    const totaleAssegnato = progettiConPercentuale.reduce((sum, a) => sum + a.percentuale, 0);
    const residuo = 100 - totaleAssegnato;

    // Trova progetti con 0%
    const progettiAZero = allocazioniMese.filter(a => a.percentuale === 0);

    if (progettiAZero.length === 0) {
      setError('Nessun progetto con 0% da calcolare');
      return;
    }

    if (residuo <= 0) {
      setError('Nessun residuo da distribuire (100% già assegnato)');
      return;
    }

    // Distribuisci equamente il residuo
    const percentualePerProgetto = Math.floor(residuo / progettiAZero.length);
    const resto = residuo % progettiAZero.length;

    try {
      for (let i = 0; i < progettiAZero.length; i++) {
        const alloc = progettiAZero[i];
        // Aggiungi 1% in più ai primi progetti per gestire il resto
        const percentuale = percentualePerProgetto + (i < resto ? 1 : 0);

        await saveAllocazione({
          id_risorsa: risorsa.id,
          id_progetto: alloc.id_progetto,
          anno,
          mese,
          percentuale,
        });
      }
      setError('');
    } catch (err: any) {
      setError(err.message || 'Errore nel calcolo automatico');
    }
  };

  const getTotaleIcon = () => {
    if (totaleMese > 100) return <ErrorIcon color="error" fontSize="small" />;
    if (totaleMese === 100) return <CheckCircleIcon color="success" fontSize="small" />;
    if (totaleMese > 0) return <WarningIcon color="warning" fontSize="small" />;
    return null;
  };

  const getTotaleColor = () => {
    if (totaleMese > 100) return 'error.main';
    if (totaleMese === 100) return 'success.main';
    if (totaleMese > 0) return 'warning.main';
    return 'text.secondary';
  };

  return (
    <Card
      ref={setNodeRef}
      sx={{
        minHeight: 200,
        bgcolor: isReadOnly ? 'action.disabledBackground' : (isOver ? 'action.hover' : 'background.paper'),
        border: '2px solid',
        borderColor: isReadOnly ? 'action.disabled' : (isOver ? 'primary.main' : 'divider'),
        transition: 'all 0.2s',
        opacity: isReadOnly ? 0.7 : 1,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {meseNome}
            </Typography>
            {isReadOnly && (
              <Chip label="Consolidato" size="small" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Pulsante copia da mese precedente */}
            {!isReadOnly && (
              <IconButton
                size="small"
                onClick={handleOpenCopyDialog}
                title="Copia pianificazione da altro mese"
                sx={{ padding: 0.5 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            )}
            {/* Pulsante calcolo automatico */}
            {!isReadOnly && allocazioniMese.some(a => a.percentuale === 0) && totaleMese < 100 && (
              <IconButton
                size="small"
                onClick={handleAutoCalculate}
                title="Calcola automaticamente le percentuali per i progetti a 0%"
                sx={{ padding: 0.5 }}
              >
                <CalculateIcon fontSize="small" />
              </IconButton>
            )}
            {/* Pulsante cancella mese */}
            {!isReadOnly && allocazioniMese.length > 0 && (
              <IconButton
                size="small"
                onClick={handleClearMonth}
                title="Cancella tutto il mese"
                sx={{ padding: 0.5 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
            {getTotaleIcon()}
            <Typography variant="body2" sx={{ color: getTotaleColor(), fontWeight: 'bold' }}>
              {totaleMese}%
            </Typography>
          </Box>
        </Box>

        {/* Progetti allocati */}
        <Stack spacing={1}>
          {allocazioniMese.map((alloc) => {
            const progetto = progetti.find((p) => p.id === alloc.id_progetto);
            const hasBudget = progetto && progetto.budget_gg_uu !== undefined && progetto.budget_gg_uu !== null && progetto.budget_gg_uu > 0;

            return (
              <Box
                key={`${alloc.id_progetto}-${mese}`}
                sx={{
                  p: 1,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: isReadOnly ? 'default' : 'pointer',
                  '&:hover': {
                    bgcolor: isReadOnly ? 'background.default' : 'action.hover',
                  },
                }}
                onClick={() => !isReadOnly && handleEditClick(alloc.id_progetto, alloc.percentuale)}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                    {getProgettoNome(alloc.id_progetto)}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {alloc.percentuale}%
                  </Typography>

                  {/* Budget Progress Bar compatta */}
                  {hasBudget && progetto && (
                    <BudgetProgressBarCompact
                      progetto={progetto}
                      percentualeAllocazione={alloc.percentuale}
                    />
                  )}
                </Box>
                {!isReadOnly && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(alloc.id_progetto);
                    }}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Stack>

        {/* Area drop per nuovi progetti */}
        {isOver && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="primary">
              Rilascia qui
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1, py: 0 }}>
            <Typography variant="caption">{error}</Typography>
          </Alert>
        )}
      </CardContent>

      {/* Dialog modifica percentuale */}
      <Dialog open={!!editingAllocazione} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Modifica Percentuale</DialogTitle>
        <DialogContent>
          {editingAllocazione && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Progetto: {getProgettoNome(editingAllocazione.id_progetto)}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="Percentuale %"
                type="number"
                value={editPercentuale}
                onChange={(e) => setEditPercentuale(e.target.value)}
                inputProps={{ min: 0 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveEdit();
                  }
                }}
              />

              {/* Pulsanti scelta rapida */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Scelta rapida:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((perc) => (
                    <Button
                      key={perc}
                      variant="outlined"
                      size="small"
                      onClick={() => handleQuickPercentuale(perc)}
                      sx={{ minWidth: '50px' }}
                    >
                      {perc}%
                    </Button>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Annulla</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog copia pianificazione */}
      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copia Pianificazione</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Copia le allocazioni da un altro mese. I progetti già presenti nel mese corrente non verranno sovrascritti.
            </Typography>

            <Autocomplete
              options={risorse}
              value={risorse.find(r => r.id === selectedRisorsaCopy) || null}
              onChange={(_, newValue) => {
                if (newValue) setSelectedRisorsaCopy(newValue.id);
              }}
              getOptionLabel={(option) => `${option.nome} ${option.cognome || ''}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Risorsa sorgente"
                  helperText="Seleziona la risorsa da cui copiare"
                />
              )}
            />

            <Autocomplete
              options={Array.from({ length: 12 }, (_, i) => i + 1)}
              value={selectedMeseCopy}
              onChange={(_, newValue) => {
                if (newValue) setSelectedMeseCopy(newValue);
              }}
              getOptionLabel={(option) => MESI[option - 1]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mese sorgente"
                  helperText="Seleziona il mese da cui copiare"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleCopyFromSelected} variant="contained">
            Copia
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const MonthGrid: React.FC<MonthGridProps> = ({ risorsa }) => {
  const { anno, allocazioni } = usePlanning();

  // Calcola mese corrente
  const oggi = new Date();
  const annoCorrente = oggi.getFullYear();
  const meseCorrente = oggi.getMonth() + 1; // 1-12

  // Visualizza sempre tutti i 12 mesi
  const mesiDaVisualizzare = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  }, []);

  // Determina se un mese è read-only (passato)
  const isMeseReadOnly = (mese: number): boolean => {
    if (anno < annoCorrente) return true;
    if (anno > annoCorrente) return false;
    return mese < meseCorrente;
  };

  // Calcola se la risorsa è completa (tutti i mesi al 100%)
  const risorsakCompleta = useMemo(() => {
    return mesiDaVisualizzare.every((mese) => {
      const totale = allocazioni
        .filter((a) => a.id_risorsa === risorsa.id && a.mese === mese && a.anno === anno)
        .reduce((sum, a) => sum + a.percentuale, 0);
      return totale === 100;
    });
  }, [allocazioni, risorsa.id, anno, mesiDaVisualizzare]);

  return (
    <Paper elevation={2} sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header risorsa */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h5">
            {risorsa.nome} {risorsa.cognome}
          </Typography>
          {risorsakCompleta && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Completa"
              color="success"
              size="small"
            />
          )}
        </Box>
        {risorsa.struttura && (
          <Typography variant="body2" color="text.secondary">
            {risorsa.struttura}
          </Typography>
        )}
        {risorsa.email && (
          <Typography variant="caption" color="text.secondary">
            {risorsa.email}
          </Typography>
        )}
      </Box>

      {/* Griglia mesi */}
      <Grid container spacing={2}>
        {mesiDaVisualizzare.map((mese) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={mese}>
            <MonthBox
              risorsa={risorsa}
              mese={mese}
              meseNome={MESI[mese - 1]}
              isReadOnly={isMeseReadOnly(mese)}
            />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default MonthGrid;
